'use client';

import { ClearIcon, SearchIcon } from '@/_assets/icon-components';
import { useCustomLocale } from '@/_hooks/useCustomLocale';
import useDebounce from '@/_hooks/useDebounce';
import { IRedux } from '@/_store/types';
import { usePathname, useRouter } from '@/i18n/navigation';
import { Box, Flex, IconButton, Input, Stack, Text } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useClickAway } from 'react-use';
import { useQuery } from '@tanstack/react-query';
import mikaService from '@/_service/mika.service';
import { findActivePoint } from '@/_utils/location';
import Loading from './Loading';
import useGTM from '@/_hooks/useGTM';

const SearchDesktop = () => {
  const [search, setSearch] = useState('');
  const [isFocus, setIsFocus] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const t = useTranslations();
  const debouncedSearchTerm = useDebounce(search, 200);
  const currentLocale = useCustomLocale();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const { country } = useSelector((state: IRedux) => state.settings);
  const { sendGTM } = useGTM();
  const { points } = useSelector((state: IRedux) => state.common);
  const { platform_id } = useSelector((state: IRedux) => state.session);
  const { data: settings } = useSelector((state: IRedux) => state.settings);

  const shipper_id = settings?.shipper_id || '';

  const activePoint = useMemo(() => {
    return findActivePoint(points);
  }, [points]);

  // Get query from URL and set it to search input when on search page
  useEffect(() => {
    const queryParam = searchParams.get('query');
    if (pathname?.includes('/search') && queryParam) {
      setSearch(decodeURIComponent(queryParam));
    } else if (!pathname?.includes('/search')) {
      // Clear search when navigating away from search page
      setSearch('');
    }
  }, [searchParams, pathname]);

  const handleSearchSubmit = (searchTerm?: string) => {
    const searchValue = searchTerm || search;
    if (searchValue.trim()) {
      setIsFocus(false); // Close dropdown when submitting
      router.push(`/search?query=${encodeURIComponent(searchValue.trim())}`);
      // Don't clear search, keep it in input
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ['search-products', debouncedSearchTerm],
    queryFn: () =>
      mikaService.search(
        {
          page: 1,
          limit: 28,
          search: debouncedSearchTerm,
          product_types: 'simple,combo,origin',
          lat: activePoint?.location?.[0] || 0,
          long: activePoint?.location?.[1] || 0,
        },
        shipper_id,
        platform_id || '',
      ),
    enabled: Boolean(debouncedSearchTerm),
  });

  useClickAway(searchBoxRef, () => {
    setIsFocus(false); // Close dropdown when clicking outside
  });

  const products =
    data?.data?.objects?.flatMap((page) => page.products || []) || [];

  useEffect(() => {
    if (products?.length > 0) {
      sendGTM('view_search_results_ads', {
        products,
        currency: country?.currency,
      });
      sendGTM('view_item_results_fb', {
        products,
        currency: country?.currency,
      });
    }
  }, [products, search, country?.currency, sendGTM]);

  return (
    <Box position='relative' flex={1} maxW='400px' ref={searchBoxRef}>
      <Flex position='relative' width='100%' alignItems='center'>
        <Box
          pl={4}
          zIndex={2}
          display='flex'
          alignItems='center'
          position='absolute'
        >
          <SearchIcon w='20' h='20' color='#333' />
        </Box>
        <Input
          border='none'
          ref={inputRef}
          value={search}
          height={'48px'}
          rounded={'full'}
          paddingLeft='48px'
          placeholder={t('search')}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocus(true)}
          backgroundColor='gray.400'
          _placeholder={{ color: 'text.muted' }}
          paddingRight={search ? '60px' : '16px'}
          onChange={(e) => setSearch(e.target.value)}
          _focus={{ borderColor: 'transparent', outline: 'none' }}
        />
        {search && (
          <IconButton
            variant='plain'
            size='sm'
            position='absolute'
            right='8px'
            onClick={() => {
              setSearch('');
              inputRef.current?.focus();
            }}
            aria-label='Clear search'
            zIndex={2}
          >
            <ClearIcon />
          </IconButton>
        )}
      </Flex>
      {isLoading && search && isFocus ? (
        <Box
          p={2}
          w={'100%'}
          zIndex={99}
          top={'55px'}
          position={'absolute'}
          borderRadius='12px'
          backgroundColor='gray.400'
          boxShadow={'0px 0px 12px 0px #45474529'}
        >
          <Loading />
        </Box>
      ) : products?.length > 0 && search && isFocus ? (
        <Box
          w={'100%'}
          bg='white'
          zIndex={99}
          p={2}
          position={'absolute'}
          top={'55px'}
          borderRadius='12px'
          boxShadow={'0px 0px 12px 0px #45474529'}
        >
          <Stack gap={0}>
            {products?.map((el) => (
              <Box
                key={el.id}
                px={4}
                py={3}
                cursor={'pointer'}
                _hover={{
                  bgColor: 'gray.400',
                }}
                borderRadius={'8px'}
                onClick={() => {
                  handleSearchSubmit(el.title?.[currentLocale] || '');
                }}
              >
                <Text fontSize='sm' color='text'>
                  {el.title?.[currentLocale]}
                </Text>
              </Box>
            ))}
          </Stack>
        </Box>
      ) : (
        search &&
        isFocus && (
          <Box
            w={'100%'}
            zIndex={99}
            p={2}
            position={'absolute'}
            bg={'white'}
            top={'55px'}
            borderRadius='12px'
            boxShadow={'0px 0px 12px 0px #45474529'}
          >
            <Flex
              flexDir={'column'}
              alignItems={'center'}
              justifyContent={'center'}
              mt={2}
              gap={2}
            >
              <Text color={'text.muted'} textAlign={'center'} fontSize={14}>
                {t('search_not_found_text')}
              </Text>
            </Flex>
          </Box>
        )
      )}
    </Box>
  );
};

export default SearchDesktop;
